// ============================================================
// MANIC MEETER — Deutsche Inhalte: Oberfläche
// ============================================================

import { PowerupId } from "../../types";
import type { UiStrings } from "../contract";

export const UI: UiStrings = {
  meta: {
    pageTitle: "Manic Meeter — Die Meeting-Hölle",
    subtitle: "Überlebe die Meeting-Hölle von MegaCorp™",
  },
  cookie: {
    text: "🍪 Diese Website verwendet Cookies und localStorage für Login-Sessions und Spielstände. Mehr Infos in unserer",
    privacyLinkLabel: "Datenschutzerklärung",
    accept: "Akzeptieren",
    decline: "Nur notwendige",
  },
  controls: {
    soundTitle: "Sound ein/ausschalten",
    soundOn: "🔊 SOUND",
    soundOff: "🔇 SOUND",
    themeTitle: "Terminal-Modus umschalten",
    themeLabel: "⬛ RETRO",
    themeLabelModern: "◻️ MODERN",
    langTitle: "Switch to English",
    langSwitchLabel: "🇬🇧 EN",
  },
  start: {
    btnLogin: "🔐 Anmelden",
    btnRegister: "📝 Registrieren",
    btnHighscores: "🏆 Highscores",
    btnAchievements: "🏅 Achievements",
    nameLabel: "Dein Name",
    namePlaceholder: "Max Mustermann",
    roleLabel: "Deine Rolle (= Schwierigkeit)",
    btnStart: "🏢 Büro betreten",
    imprint: "Impressum",
    privacy: "Datenschutz",
    anonymousName: "Anon",
  },
  authStatus: {
    loggedIn: (username) => `✅ Angemeldet als <strong>${username}</strong>`,
    logout: "Abmelden",
    guest: "👤 Gastsitzung · <em>Registriere dich, um Fortschritt zu speichern!</em>",
    anonymous: "👤 Nicht angemeldet · Spielen als Gast möglich",
  },
  instructions: {
    heading: "📋 Spielanleitung",
    sections: [
      {
        heading: "🎯 Ziel",
        paragraphs: [
          "Überlebe so viele Meetings wie möglich, ohne einzuschlafen oder unter den Mindestlohn von <strong>1.000€</strong> zu fallen.",
        ],
      },
      {
        heading: "😴 Müdigkeit",
        paragraphs: [
          "Jedes Mal, wenn ein Kollege redet, steigt deine Müdigkeit. Ist sie voll, schläfst du ein — Gehaltsabzug!",
        ],
      },
      {
        heading: "🎮 Dein Zug",
        paragraphs: [
          "<strong>😎 Prokrastinieren</strong> — Reduziert Müdigkeit. Aber Vorsicht: Wirst du dabei erwischt, gibt's Gehaltsabzug!",
          "<strong>🎤 Aktion</strong> — Wenn du angesprochen wirst, musst du antworten (Fragen, Minispiele). Wirst du nicht angesprochen, hältst du einen unnötigen Monolog.",
        ],
      },
      {
        heading: "⚡ Angesprochen werden",
        paragraphs: [
          'Kollegen können dich jederzeit ansprechen — achte auf die <span class="address-hint">farblich hervorgehobenen</span> Hinweise! Die Häufigkeit hängt von deiner Rolle ab.',
        ],
      },
      {
        heading: "🃏 Rüstzeit & Powerups",
        paragraphs: [
          "Vor jedem Meeting ziehst du eine Karte — mit Glück findest du ein nützliches Powerup. Im Shop kannst du mit <strong>Impostor-Punkten</strong> weitere Upgrades kaufen.",
        ],
      },
      {
        heading: "🎭 Impostor-Punkte",
        paragraphs: [
          "Für jeden überlebten Tag bekommst du Punkte. Bonus, wenn du nicht eingeschlafen bist! Die Punkte bleiben auch nach Game Over erhalten.",
        ],
      },
      {
        heading: "⌨️ Steuerung",
        paragraphs: [
          "<strong>Maus / Touch</strong> — Buttons klicken für Aktionen und Antworten.",
          "<strong>Escape</strong> — Spiel beenden und zum Startbildschirm zurückkehren.",
        ],
      },
    ],
    okBtn: "✅ Verstanden — Los geht's!",
  },
  status: {
    day: "Tag",
    salary: "Gehalt",
    fatigue: "Müdigkeit",
    meetings: "Meetings",
    points: "Impostor-Punkte",
  },
  infoPanel: {
    toggleClosed: "📋 Info ▼",
    toggleOpen: "📋 Info ▲",
    perksHeading: "🏷️ Perks",
    powerupsHeading: "🎒 Powerups",
    statsHeading: "📊 Statistiken",
    noPerks: "Keine aktiven Perks",
    noPowerups: "Keine aktiven Powerups",
    wholeMeeting: "Meeting",
    turnsLeft: (turns) => `${turns} Zug/Züge`,
    turnsShort: (turns) => `${turns}T`,
    remaining: "verbleibend",
  },
  meeting: {
    you: "🧑 DU",
    directionForward: "→ Vorwärts",
    directionBackward: "← Rückwärts",
    participants: (count) => `👥 ${count} Teilnehmer`,
    rounds: (count) => `${count} Runde(n)`,
    tasks: (count) => `${count} Aufgaben`,
    roundEnded: (roundsLeft) => `── Runde beendet. Noch ${roundsLeft} Runde(n) ──`,
    directionReversed: "🔀 Reihenfolge dreht sich um!",
    next: (meetingName) => `📬 Nächstes Meeting: <strong>${meetingName}</strong>`,
    survived: (meetingName) => `✅ Meeting "${meetingName}" überlebt!`,
    enter: "🚪 Meeting betreten",
    enterOnline: "📡 Online-Meeting beitreten",
    continueToNext: "Weiter zum nächsten Meeting",
    schedule: "Heutige Termine",
    scheduleToday: (count) => `${count} Meeting(s) heute.`,
  },
  actions: {
    procrastinate: "😎 Prokrastinieren",
    action: "🎤 Aktion",
    powerup: "🎒 Powerup",
    skip: "⏭ Überspringen",
    continue: "Weiter",
    walkOn: "🚶 Weitergehen",
    done: "✅ Fertig",
    close: "Schließen",
    startDay: "Tag starten",
    endOfDay: "Feierabend! Nächster Tag →",
    playAgain: "🔄 Nochmal spielen",
    yourAnswer: (answer) => `💬 Deine Antwort: "${answer}"`,
    noAnswer: "⏰ Keine Antwort...",
    powerupUsedTurnOver: "⏰ Du hast ein Powerup benutzt. Zug vorbei.",
    staredIntoSpace: "⏰ Du starrst ins Leere… nichts passiert.",
  },
  minigames: {
    announce: {
      question: "FRAGE!",
      brainstorming: "BRAINSTORMING!",
      teamBuilding: "TEAM-BUILDING!",
      buzzwordBingo: "BUZZWORD-BINGO!",
      emailSorting: "E-MAIL-SORTIEREN!",
      calendarTetris: "KALENDER-TETRIS!",
      codeReview: "CODE REVIEW!",
      aiHallucination: "KI-HALLUZINATION!",
    },
    announceLine: (headline, context) => `⚡ <strong>${headline}</strong> ${context}`,
    assistantAnnounce: (tool) => `Assistent: <strong>${tool}</strong> — markiere, was er sich ausgedacht hat!`,
    memoriseOrderShort: "Merke dir die Reihenfolge und klicke aus dem Gedächtnis!",
    typeLetterByLetter: (word) => `Tippe Buchstabe für Buchstabe: <strong>${word}</strong>`,
    typeHint: "Tippe Buchstabe für Buchstabe!",
    repeatSequence: "Mach die Sequenz nach! (W=↑ S=↓ A=← D=→)",
    sequenceHint: "W=↑  S=↓  A=←  D=→",
    memoriseOrder: "Merke dir die Reihenfolge und klicke die Buzzwords aus dem Gedächtnis!",
    memorising: "Merke dir die Reihenfolge...",
    clickInOrder: (total) => `Klicke in der gemerkten Reihenfolge: 1/${total}`,
    sortEmails: "Sortiere jede E-Mail: Spam, Wichtig oder Delegieren!",
    emailSpam: "🚫 Spam",
    emailImportant: "⚡ Wichtig",
    emailDelegate: "👋 Delegieren",
    findFreeSlot: "Finde den Zeitslot, in dem ALLE Teilnehmer frei sind!",
    slotQuestion: "Wann haben ALLE Teilnehmer Zeit? Klicke auf den Zeitslot!",
    codeLanguage: (language) => `Sprache: <strong>${language}</strong> — Finde den Fix!`,
    findTheFix: "Finde den Fix!",
    assistantLabel: (tool) => `🤖 ${tool}`,
    promptLabel: (task) => `Prompt: "${task}"`,
    markHallucinations: (count) => `Markiere ${count} erfundene Aussage${count === 1 ? "" : "n"}.`,
    evaluating: "Auswertung...",
    correct: "✓ Richtig!",
    wrong: (expected) => `✗ Falsch! Erwartet: ${expected}`,
    wrongOrder: "✗ Falsche Reihenfolge! Von vorn.",
    correctWas: (correct) => `✗ Falsch! War: ${correct}`,
    progress: (done, total) => `✓ ${done}/${total}`,
    slotCorrect: (slot) => `✓ Richtig! Um ${slot} haben alle Zeit!`,
    slotWrong: (slot) => `✗ Falsch! ${slot} war der freie Slot!`,
    hallucinationsCaught: (hits, wanted) => `✓ ${hits}/${wanted} Halluzinationen erwischt!`,
    hallucinationsMissed: (hits, wanted) => `✗ Nur ${hits}/${wanted} erwischt. Der Rest geht so raus.`,
  },
  outcomes: {
    brainstormWin: "✅ Perfekt! Buzzword vernichtet. (-10 Müdigkeit)",
    brainstormFail: "❌ Zu langsam! Meeting wird verlängert.",
    simonWin: "✅ Synchronized! Team Spirit +1. (-8 Müdigkeit)",
    simonFail: "❌ Aus dem Takt! Peinlich. (+5 Müdigkeit)",
    bingoWin: "✅ Bingo! Du kennst deine Buzzwords. (-12 Müdigkeit)",
    bingoFail: "❌ Falsche Reihenfolge! Peinliche Stille. (+5 Müdigkeit)",
    emailPerfect: (total) => `✅ Alle ${total} richtig! Inbox Zero! (-15 Müdigkeit)`,
    emailPartial: (correct, total) => `⚠️ ${correct}/${total} richtig. Naja, geht so. (-5 Müdigkeit)`,
    emailFail: "❌ Keine richtig! Dein Postfach explodiert. (+8 Müdigkeit)",
    kalenderWin: "✅ Perfekt eingetaktet! Du bist ein Kalender-Profi. (-10 Müdigkeit)",
    kalenderFail: "❌ Doppelbuchung! Der Chef ist sauer. (+5 Müdigkeit)",
    codeReviewWin: "✅ Bug gefixed! Sauberer Code. (-12 Müdigkeit)",
    codeReviewFail: "❌ Falscher Fix! Der Bug ist jetzt ein Feature. (+5 Müdigkeit)",
    kiPerfect: (hits, total) => `✅ ${hits}/${total} — alle Halluzinationen erwischt! Die Folie ist jetzt sogar wahr. (-12 Müdigkeit)`,
    kiPartial: (hits, total) => `⚠️ ${hits}/${total} erwischt. Der Rest steht jetzt im Protokoll. (-5 Müdigkeit)`,
    kiFail: "❌ Nichts erwischt! Es glaubt jetzt die halbe Firma. (+6 Müdigkeit)",
  },
  boss: {
    intro: (tasks) => `🏛️ <strong>VORSTANDSMEETING!</strong> Der Vorstand hat ${tasks} Aufgaben für dich. Kein Prokrastinieren möglich!`,
    stakes: (penalty, bonus) => `💰 Falsche Antwort = -${penalty}€ | Alle richtig = +${bonus}€ Bonus!`,
    question: (prompt) => `❓ <strong>FRAGE!</strong> ${prompt}`,
    taskHeader: (current, total) => `── Aufgabe ${current} von ${total} ──`,
    announce: "🏛️ <strong>Vorstandsmeeting!</strong> Kein Entkommen.",
    noEscape: "Kein Entkommen.",
    enter: "🏛️ Vorstandsmeeting betreten",
    survived: "✅ Vorstandsmeeting überlebt!",
    questionWin: "✅ Richtig! Der Vorstand nickt anerkennend.",
    questionFail: (penalty) => `❌ Falsch! Enttäuschte Blicke im Vorstand. -${penalty}€`,
    brainstormWin: "✅ Perfekt! Der Vorstand ist beeindruckt.",
    brainstormFail: (penalty) => `❌ Zu langsam! Der CFO schüttelt den Kopf. -${penalty}€`,
    simonWin: "✅ Synchronized! Der Vorstand applaudiert.",
    simonFail: (penalty) => `❌ Aus dem Takt! Peinlich vor dem Vorstand. -${penalty}€`,
    bingoWin: "✅ Bingo! Sprachkompetenz bestätigt.",
    bingoFail: (penalty) => `❌ Falsche Reihenfolge! Der Vorstand notiert sich das. -${penalty}€`,
    emailWin: (total) => `✅ Alle ${total} richtig! Inbox-Management auf Vorstandsniveau.`,
    emailFail: (correct, total, penalty) => `❌ ${correct}/${total} richtig. Der Vorstand ist nicht überzeugt. -${penalty}€`,
    kalenderWin: "✅ Perfekt eingetaktet! Zeitmanagement-Profi.",
    kalenderFail: (penalty) => `❌ Doppelbuchung! Der Vorstand seufzt. -${penalty}€`,
    codeReviewWin: "✅ Bug gefixed! Clean Code approved.",
    codeReviewFail: (penalty) => `❌ Falscher Fix! Der CTO schüttelt den Kopf. -${penalty}€`,
    kiWin: "✅ Alle Halluzinationen erwischt! Der Vorstand erfährt nie davon.",
    kiFail: (hits, total, penalty) => `❌ ${hits}/${total} erwischt. Der Vorstand zitiert jetzt eine Studie, die es nicht gibt. -${penalty}€`,
    flawless: (bonus) => `🎉 <strong>Perfekt!</strong> Kein Fehler! Der Vorstand ist begeistert. +${bonus}€ Gehaltserhöhung!`,
    withMistakes: (mistakes, bonus) => `✅ Vorstandsmeeting überlebt mit ${mistakes} Fehler(n). +${bonus}€`,
    noBonus: (mistakes) => `😰 Vorstandsmeeting überlebt, aber ${mistakes} Fehler. Kein Bonus.`,
  },
  prep: {
    heading: "🃏 <strong>Rüstzeit!</strong> Ziehe eine von 3 Karten — vielleicht findest du etwas Nützliches!",
    card: (index) => `🃏 Karte ${index}`,
    emptyCard: "📋 Leere Karte. Kein Glück diesmal.",
    found: (name, description) => `🎉 <strong>${name}!</strong> ${description}`,
    pizzaSkip: "🍕 Pizza ist da! Du überspringst dieses Meeting komplett.",
    vpnSkip: "🔒 <strong>VPN-Zugang!</strong> Du hast dich erfolgreich aus dem Meeting geschlichen.",
    vpnSkipped: (meetingName) => `📬 "${meetingName}" wurde übersprungen.`,
  },
  powerups: {
    activation: {
      [PowerupId.Kaffee]: "☕ {name}: −25 Müdigkeit!",
      [PowerupId.HandyAkku]: "📱 {name}: Nächstes Prokrastinieren ist absolut sicher!",
      [PowerupId.NoiseCancelling]: "🎧 {name}: Müdigkeit durch Kollegen −50% dieses Meeting!",
      [PowerupId.FakeNotizen]: "📋 {name}: Nächste Frage wird automatisch richtig beantwortet!",
      [PowerupId.LaptopTarnung]: "💻 {name}: Prokrastinieren sieht 3 Runden wie Arbeiten aus!",
      [PowerupId.PizzaBestellen]: "🍕 {name}: Du überspringst dieses Meeting komplett!",
      [PowerupId.MuteButton]: "🔇 {name}: Immun gegen Ansprechen dieses Meeting!",
      [PowerupId.RemoteJoker]: "📡 {name}: Meeting wird online! Geringere Ansprech-Chance!",
      [PowerupId.BullshitBingo]: "📊 {name}: Buzzwords werden gezählt — Bonus am Meeting-Ende!",
      [PowerupId.PromptBibliothek]: "📚 {name}: Die Prompts liegen bereit — alle Minispiel-Timer +60%!",
      [PowerupId.KiZusammenfassung]: "📝 {name}: Die KI hat schon zusammengefasst — 2 Runden weniger!",
      [PowerupId.KiAgent]: "🤖 {name}: Hat nachts durchgearbeitet — +150€, aber das Gegenlesen kostet Nerven (+10 Müdigkeit)!",
    },
    useHeading: "🎒 Powerup einsetzen?",
    inventoryCount: (count) => `(${count}×)`,
  },
  shop: {
    heading: "🛒 Shop",
    points: "Impostor-Punkte",
    statUpgrades: "📈 Stat-Upgrades",
    powerups: "🎒 Powerups",
    permanentUnlocks: "🏆 Permanente Freischaltungen",
    permanentHint: "(Freigeschaltet bei genügend Gesamt-Punkten — keine Kosten!)",
    completed: "✅ Abgeschlossen",
    level: (level, max) => `Level ${level}/${max}`,
    owned: (count) => `📦 ${count}×`,
    usableLabel: "Nutzbar",
    usablePrep: "Rüstzeit",
    usableMeeting: "Meeting",
    usableBoth: "Rüstzeit + Meeting",
    effect: "Effekt",
    maxed: "MAX",
    unlockFrom: (cost) => `Ab ${cost} 🎭 gesamt`,
    close: "✅ Fertig",
  },
  vendor: {
    heading: "🛍️ Gelegenheit",
    yourSalary: "💰 Dein Gehalt",
    walkOn: "🚶 Weitergehen",
  },
  day: {
    welcome: (name, role) => `Willkommen, <strong>${name}</strong>! Du bist ${role} bei <strong>MegaCorp™</strong>.`,
    startingSalary: (salary, minWage) => `Startgehalt: ${salary}€. Unter ${minWage}€ = Game Over.`,
    goal: "Überlebe so viele Meetings wie möglich!",
    dayHeader: (day, meetings) => `📅 Tag ${day} — ${meetings} Meeting(s) heute.`,
    activePerks: (perks) => `🏷️ Aktive Perks: ${perks}`,
    attestActive: (days) => `📋 <strong>ATTEST aktiv!</strong> Noch ${days} Tag(e) — massive Boni!`,
    attestFound: "📋🏆 <strong>ATTEST GEFUNDEN!</strong> Du hast ein ärztliches Attest! 5 Tage lang unfassbare Boni!",
    attestEffects: "⚡ -70% Müdigkeit, 3x Erholung, -80% Ansprech-Chance, -90% Strafen, +100€/Tag!",
    perkBonus: "Tagesbonus",
    perkMalus: "Tagesmalus",
    perkLine: (emoji, kind, name, description) => `${emoji} <strong>${kind}:</strong> ${name} — ${description}`,
    perkLateGaming: "🎮 Lange gezockt letzte Nacht... (+20 Müdigkeit)",
    perkTraffic: "🚗 Stau gehabt... (+15 Müdigkeit)",
    meetingCancelled: (meetingName) => `⏭️ "${meetingName}" fällt aus!`,
    homeoffice: "🏠 <strong>HOMEOFFICE!</strong> Du arbeitest heute von zuhause — alle Meetings sind online!",
    weekendBonusSalary: (amount, event) => `💰 +${amount}€ Bonus von "${event}"!`,
    weekendBonusFatigue: (amount, event) => `☕ -${amount} Müdigkeit durch "${event}"!`,
    weekendExtraMeetings: (count, event) => `📅 +${count} Extra-Meeting(s) durch "${event}"!`,
    daySurvived: (day) => `☀️ Tag ${day} geschafft!`,
    pointsEarned: (points, noSleepBonus, total) =>
      `🎭 +${points} Impostor-Punkte${noSleepBonus ? " (Bonus: nicht eingeschlafen!)" : ""}! Gesamt: ${total}`,
    newAchievements: (list) => `🏆 Neue Achievements: ${list}`,
    summaryHeading: "Tagesbilanz",
    chartMeetingsToday: "📊 Meetings heute",
    chartFatigueCausers: "😴 Müdigkeits-Verursacher (gesamt)",
    chartDeadliest: "💀 Gefährlichste Meetings (gesamt)",
  },
  panels: {
    statMeetings: "Meetings",
    statCaught: "Erwischt",
    statFellAsleep: "Eingeschlafen",
    statMinigames: "Minigames",
    statProcrastinated: "Prokrastiniert",
    attestPill: (days) => `📋 Attest (${days}T)`,
    attestTooltip: (days) => `Ärztliches Attest — massive Boni für ${days} Tag(e)`,
    online: "📡 ONLINE",
    offline: "🏢 OFFLINE",
    round: (current, total) => `🔄 Runde ${current}/${total}`,
    newDayHeading: (day) => `☀️ Tag ${day} — Ein neuer Tag im Büro`,
    todaysPlan: "Heute auf dem Plan:",
    prepHeading: "☕ Rüstzeit",
    prepNextMeeting: (meetingName) => `Nächstes Meeting: <strong>${meetingName}</strong>`,
    prepHint: "Ziehe eine Karte — vielleicht wird das Meeting remote!",
    summaryMeetingsSurvived: "🏆 Meetings überlebt",
    summaryMeetingsSkipped: "⏭️ Meetings übersprungen",
    summaryTimesCaught: "💀 Mal erwischt",
    summaryTimesFellAsleep: "😴 Mal eingeschlafen",
    summaryMonologues: "🎤 Monologe",
    summaryMinigamesWon: "🎮 Minigames gewonnen",
    summaryMinigamesLost: "🎮 Minigames verloren",
    summarySalary: "💰 Gehalt",
    summaryRecord: "Rekord",
    summaryFatigue: "😴 Müdigkeit",
    finalDaysSurvived: "📊 Tage überlebt",
    finalMeetingsSurvived: "🏆 Meetings überlebt",
    finalPeakSalary: "💰 Höchstes Gehalt",
    weekendHint: "⚠️ Bonus-Meetings! Aber es gibt auch Belohnungen...",
    endingsCollection: "📖 Enden-Sammlung",
    achievementsModalHeading: (unlocked, total) => `🏆 Achievements (${unlocked}/${total})`,
  },
  encounter: {
    fatigueDelta: (sign, amount) => `😴 ${sign}${amount} Müdigkeit`,
    salaryDelta: (sign, amount) => `💰 ${sign}${amount}€ Gehalt`,
    newPerk: (emoji, name, description) => `${emoji} Neuer Perk: <strong>${name}</strong> — ${description}`,
    perkRemoved: (name) => `✨ Perk entfernt: <strong>${name}</strong>`,
    powerupFound: (name, description) => `🎁 <strong>Powerup gefunden:</strong> ${name} — ${description}`,
  },
  bingo: {
    complete: "🎯 <strong>BINGO!</strong> Alle Ziele erreicht! -20 Müdigkeit & +100€ Bonus!",
    heading: "🎯 Meeting-Bingo",
    bingoLine: "🎉 BINGO!",
    progress: (done, total) => `(${done}/${total})`,
    buzzwordBonus: (count, bonus) => `📊 Bullshit-Bingo! ${count} Buzzwords gehört — <strong>-${bonus} Müdigkeit!</strong>`,
  },
  gameOver: {
    heading: "💀 GAME OVER 💀",
    bonusPoints: (points) => `🎭 +${points} Bonus-Impostor-Punkte für dieses Ende!`,
    keptPoints: (points, total) => `🎭 Impostor-Punkte behalten: <strong>${points}</strong> (Gesamt verdient: ${total})`,
    unknownEnding: "???",
    playAgain: "🔄 Nochmal spielen",
  },
  escape: {
    heading: "⏸️ Spiel beenden?",
    body: "Dein Fortschritt (Impostor-Punkte & Upgrades) wird gespeichert. Der aktuelle Run wird beendet.",
    confirm: "🚪 Beenden",
    cancel: "↩️ Weiterspielen",
  },
  achievements: {
    heading: "🏅 Achievements",
    unlocked: (emoji, name, description) => `🏆 <strong>ACHIEVEMENT FREIGESCHALTET:</strong> ${emoji} ${name} — ${description}`,
    unlockedOf: (unlocked, total) => `${unlocked}/${total} Achievements freigeschaltet`,
    locked: "🔒",
  },
  auth: {
    loginHeading: "🔐 Anmelden",
    registerHeading: "📝 Registrieren",
    usernameOrEmail: "Benutzername oder E-Mail",
    username: "Benutzername",
    email: "E-Mail",
    password: "Passwort",
    passwordMin: "Passwort (min. 8 Zeichen)",
    passwordConfirm: "Passwort bestätigen",
    submitLogin: "Anmelden",
    submitRegister: "Registrieren",
    cancel: "Abbrechen",
    fillAllFields: "Alle Felder ausfüllen.",
    passwordMismatch: "Passwörter stimmen nicht überein.",
    passwordTooShort: "Passwort muss mindestens 8 Zeichen haben.",
    loginFailed: "Anmeldung fehlgeschlagen.",
    registerFailed: "Registrierung fehlgeschlagen.",
  },
  highscores: {
    heading: "🏆 Highscores",
    tabScore: "🏅 Score",
    tabDays: "📅 Tage",
    tabSalary: "💰 Gehalt",
    tabMeetings: "🏆 Meetings",
    colName: "Name",
    colRole: "Rolle",
    colScore: "Score",
    colDaysMeetings: "Tage/Meetings",
    colSalary: "Gehalt",
    empty: "Noch keine Highscores.",
    close: "Schließen",
  },
  api: {
    serverError: "Serverfehler",
    connectionFailed: "Verbindung zum Server fehlgeschlagen.",
  },
  gameOverReasons: {
    belowMinWage: (salary, minWage) =>
      `Dein Gehalt (${salary}€) ist unter den Mindestlohn (${minWage}€) gefallen. Du wurdest gekündigt.`,
  },
  turns: {
    caught: (action, penalty, escalation) => `💀 ${action} Gehaltskürzung: -${penalty}€${escalation}`,
    escalation: (times) => ` (${times}x Eskalation!)`,
    procrastinateCritical: (action, reduction) => `⚡ ${action} CRITICAL! (-${reduction} Müdigkeit)`,
    monologue: (rounds) => `🎤 Du hältst einen ungefragten Monolog. Meeting verlängert sich um ${rounds} Runde(n)!`,
    answerCritical: (reduction) => `✅⚡ CRITICAL! Gute Antwort! (-${reduction} Müdigkeit)`,
    answerGood: (reduction) => `✅ Gute Antwort! Das Meeting geht normal weiter. (-${reduction} Müdigkeit)`,
    answerWrong: (rounds) => `❌ Falsche Antwort! Diskussion ausgelöst. +${rounds} Runde(n)!`,
    fellAsleep: (penalty) => `😴💤 Du bist EINGESCHLAFEN! Einlauf vom Chef. Gehaltskürzung: -${penalty}€!`,
    fakeNotes: "📋 Fake-Notizen! Du tust so, als hättest du alles mitgeschrieben.",
  },
};
